// TanStack Query keys + shared query hooks. Every screen that needs
// the dashboard payload imports the same hook so the Shell's
// initial fetch is reused across screens (single QueryCache entry).

import { useCallback } from "react";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { isReportTerminal } from "@bitacorapro/core-engine";
import {
  type AssetMaintenanceItemsResponse,
  type AssetOwnerInvitationCreateRequest,
  type BlockItemsResponse,
  type AssetsResolveRequest,
  type CarteraDashboardAssetsQuery,
  type CarteraDashboardGroupsQuery,
  type ChecklistTemplateUpdateBody,
  type FindingClassificationBody,
  type FindingConformityBody,
  type FindingObservationStatusBody,
  type FindingVisitBody,
  OWNER_INQUIRY_ATTACHMENT_URLS_BATCH_MAX,
  type OrgSelfServiceInvitationCreateRequest,
  type OwnerInquiryReplyRequest,
  type OwnerInquiryConversionRequest,
  type OwnerInquiryConversionSuggestionRequest,
  type OwnerInquiryDetail,
  type PhotoReviewRequest,
  type PhotoReviewResponse,
  type ReportPhotosResponse,
  type ShareBundleCreateBody,
  type StoreComparisonQuery,
  type UnitDeliveryBody,
} from "@bitacorapro/api-types";
import { ApiError } from "./error";
import { assetsApi } from "./endpoints/assets";
import { authApi } from "./endpoints/auth";
import { carteraDashboardApi } from "./endpoints/cartera-dashboard";
import { checklistTemplatesApi } from "./endpoints/checklist-templates";
import {
  classifyFinding,
  getFinding,
  postFindingConformity,
  scheduleFindingVisit,
  updateFindingObservationStatus,
} from "./endpoints/findings";
import { getFacilityDashboard } from "./endpoints/facility-dashboard";
import { getCompareStores } from "./endpoints/ia";
import { maintenanceApi } from "./endpoints/maintenance";
import { intervaloDelHilo } from "../postventa/consultasList";
import { organizationsApi } from "./endpoints/organizations";
import {
  getOwnerInquiries,
  getOwnerInquiry,
  getOwnerInquiryAttachmentUrls,
  markOwnerInquiryRead,
  replyOwnerInquiry,
} from "./endpoints/owner-inquiries";
import {
  convertOwnerInquiry,
  suggestOwnerInquiryConversion,
} from "./endpoints/owner-inquiry-conversion";
import {
  generarPostventaAnalisis,
  getPostventaAnalisis,
  getPostventaClosureSummary,
  getPostventaObservations,
  getPostventaSurveySummary,
  getPostventaTaxonomyMetrics,
} from "./endpoints/postventa-dashboard";
import type {
  FindingAssignBatchBody,
  FindingAssignBody,
  ProviderCreateBody,
} from "@bitacorapro/api-types";
import {
  assignFinding,
  assignFindingBatch,
  createProvider,
  listProviders,
} from "./endpoints/providers";
import { recipesApi } from "./endpoints/recipes";
import { reportsApi } from "./endpoints/reports";
import { shareBundlesApi } from "./endpoints/share-bundles";

export const queryKeys = {
  facilityDashboard: ["facility-dashboard"] as const,
  recipeMe: ["recipes", "me"] as const,
  postventaObservations: ["postventa-dashboard", "observations"] as const,
  providers: ["providers"] as const,
  postventaTaxonomyMetrics: ["postventa-dashboard", "taxonomy-metrics"] as const,
  postventaClosureSummary: ["postventa-dashboard", "closure-summary"] as const,
  postventaSurveySummary: ["postventa-dashboard", "survey-summary"] as const,
  // ADR-0154 — un informe por alcance: la cartera (sin id) o un proyecto.
  postventaAnalisis: (assetGroupId?: string) =>
    ["postventa-dashboard", "analisis", assetGroupId ?? "cartera"] as const,
  shareBundles: ["share-bundles"] as const,
  memberships: ["auth", "memberships"] as const,
  // Distinct key from `memberships` — the roster (view=roster) response
  // is a different, filtered shape, never mixed into the same cache
  // entry the unfiltered actor-name-resolution readers rely on.
  roster: ["auth", "memberships", "roster"] as const,
  recipe: ["recipe", "me"] as const,
  myOrganization: ["organizations", "me"] as const,
  carteraProyectos: (query: CarteraDashboardGroupsQuery = {}) =>
    ["cartera-dashboard", "groups", query] as const,
  carteraProyectoDetail: (id: string, query: CarteraDashboardAssetsQuery = {}) =>
    ["cartera-dashboard", "groups", id, "assets", query] as const,
  // The prefix both of the two keys above share. Activating a unit
  // changes the row's assetStatus AND the project's aggregate counts, and
  // the caller has no way to know which of the many (query)-parameterized
  // entries are live — so the prefix, not a single key, is the
  // invalidation unit.
  carteraDashboardAll: ["cartera-dashboard", "groups"] as const,
  resolveScopes: (req: AssetsResolveRequest) =>
    [
      "assets",
      "resolve",
      // Sorted so equal id sets dedupe regardless of input order.
      [...(req.brandIds ?? [])].sort(),
      [...(req.assetGroupIds ?? [])].sort(),
      [...(req.assetIds ?? [])].sort(),
    ] as const,
  assignableAssets: ["assets", "assignable"] as const,
  brandAffiliations: ["organizations", "me", "brand-affiliations"] as const,
  assetMaintenanceItems: (assetId: string) => ["assets", assetId, "maintenance-items"] as const,
  maintenanceItemHistory: (assetId: string, itemId: string) =>
    ["assets", assetId, "maintenance-items", itemId, "events"] as const,
  visitPhotos: (reportId: string) => ["reports", reportId, "photos"] as const,
  // Suffixed so this key is never a strict prefix of visitPhotos /
  // reportBlocks / reportBlockItems: TanStack matches invalidations by
  // prefix, so a bare ["reports", id] would sweep the Infinity-staleTime
  // bloque caches along with the detail row.
  reportDetail: (reportId: string) => ["reports", reportId, "detail"] as const,
  reportBlocks: (reportId: string) => ["reports", reportId, "blocks"] as const,
  reportBlockItems: (reportId: string, areaId: string) =>
    ["reports", reportId, "blocks", areaId, "items"] as const,
  assetObservations: (assetId: string) => ["assets", assetId, "observations"] as const,
  assetState: (assetId: string) => ["assets", assetId, "state"] as const,
  visitContact: (assetId: string) => ["assets", assetId, "visit-contact"] as const,
  checklistTemplatesAdminList: ["checklist-templates", "admin", "list"] as const,
  checklistTemplateAssets: (templateId: string) =>
    ["checklist-templates", templateId, "assets"] as const,
  finding: (id: string) => ["findings", id] as const,
  // ADR-0145 — consultas del propietario. `ownerInquiriesAll` es el
  // prefijo de la lista Y del detalle (una respuesta mueve las dos);
  // `ownerInquiriesLists` sólo las listas (una marca de lectura mueve el
  // punto «nueva» de la fila, no el hilo abierto).
  ownerInquiriesAll: ["owner-inquiries"] as const,
  ownerInquiriesLists: ["owner-inquiries", "group"] as const,
  ownerInquiries: (assetGroupId: string) => ["owner-inquiries", "group", assetGroupId] as const,
  ownerInquiry: (id: string) => ["owner-inquiries", "detail", id] as const,
  // ADR-0158 — URL firmadas de los adjuntos de UN mensaje. Bajo el prefijo
  // de consultas, pero responder NO las renueva (ver useReplyOwnerInquiry).
  ownerInquiryAttachmentUrls: (id: string, ids: readonly string[]) =>
    ["owner-inquiries", "attachment-urls", id, ids] as const,
  compareStores: (query: StoreComparisonQuery) => ["assets", "compare", query] as const,
};

// `enabled` defaults to true for backward compat, but every call site
// that mounts unconditionally inside Shell (Sidebar, CommandPalette,
// Equipo) must pass `isFacilityDashboardEnabled(recipe)` explicitly —
// TanStack Query's `enabled` is per-observer, so a single un-gated
// mount anywhere still fires the request and 403s for non-facility
// orgs (ADR-0069 Phase B).
export function useFacilityDashboard(enabled = true) {
  return useQuery({
    queryKey: queryKeys.facilityDashboard,
    queryFn: getFacilityDashboard,
    enabled,
  });
}

// «Comparar tiendas» (ADR-0167) — the IA screen's deterministic table.
// A minute of staleTime: a second tap on the chip reuses the same
// figures (and the same «al …» stamp) instead of re-reading the batch.
export function useCompareStores(query: StoreComparisonQuery, enabled = true) {
  return useQuery({
    queryKey: queryKeys.compareStores(query),
    queryFn: ({ signal }) => getCompareStores(query, signal),
    staleTime: 60_000,
    enabled,
  });
}

// Per-asset maintenance items (ADR-0083) — the store detail screen's
// "Mantenciones" panel source. Disabled until an assetId is known so the
// hook is safe to call before the route param / dashboard data has
// resolved.
export function useAssetMaintenanceItems(assetId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.assetMaintenanceItems(assetId ?? ""),
    queryFn: () => maintenanceApi.listForAsset(assetId as string),
    enabled: !!assetId,
  });
}

// Per-item service history (ADR-0086) — the detail popover's
// "Historial" source. Disabled until both ids are known, same pattern
// as useAssetMaintenanceItems above.
export function useMaintenanceItemHistory(assetId: string | undefined, itemId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.maintenanceItemHistory(assetId ?? "", itemId ?? ""),
    queryFn: () => maintenanceApi.history(assetId as string, itemId as string),
    enabled: !!assetId && !!itemId,
  });
}

// "Marcar mantención realizada" (ADR-0083, permission widened ADR-0086)
// — patches the panel cache directly from the response rather than
// invalidating it, same precedent as useUpdateChecklistTemplateAssets
// below: the response already carries the authoritative new
// lastServicedAt. The history query, by contrast, IS invalidated rather
// than patched — a new event is a new row in a list this mutation has
// no server-assigned id/servicedAt/recordedAt for until the next fetch,
// unlike the panel's single lastServicedAt scalar. Without this, a
// freshly-marked item's "Historial" reads stale until an unrelated
// remount, which on an audit surface looks like data loss, not latency.
export function useMarkMaintenanceItemServiced() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      assetId,
      itemId,
      note,
      servicedAt,
    }: {
      assetId: string;
      itemId: string;
      note?: string;
      servicedAt?: string;
    }) =>
      maintenanceApi.markServiced(assetId, itemId, {
        ...(note ? { note } : {}),
        ...(servicedAt ? { servicedAt } : {}),
      }),
    onSuccess: (data, { assetId, itemId }) => {
      qc.setQueryData<AssetMaintenanceItemsResponse>(
        queryKeys.assetMaintenanceItems(assetId),
        (prev) =>
          prev && {
            items: prev.items.map((it) =>
              it.id === data.itemId ? { ...it, lastServicedAt: data.lastServicedAt } : it
            ),
          }
      );
      void qc.invalidateQueries({ queryKey: queryKeys.maintenanceItemHistory(assetId, itemId) });
    },
  });
}

// Void a mark (ADR-0086) — the compensating control. Invalidates both
// caches rather than patching: a void changes both the history row (now
// carrying voidedAt/voidedBy/voidReason) and, if the voided mark was the
// most recent one, the panel's lastServicedAt (which must fall back to
// whatever non-voided mark is now most recent, or to never-serviced —
// not a value this mutation's response carries).
export function useVoidMaintenanceItemEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      assetId,
      itemId,
      eventId,
      reason,
    }: {
      assetId: string;
      itemId: string;
      eventId: string;
      reason?: string;
    }) => maintenanceApi.voidEvent(assetId, itemId, eventId, reason),
    onSuccess: (_data, { assetId, itemId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.assetMaintenanceItems(assetId) });
      void qc.invalidateQueries({ queryKey: queryKeys.maintenanceItemHistory(assetId, itemId) });
    },
  });
}

// The store detail screen's photo gallery (.vgal/.vlb), lazy per visit —
// deliberately not embedded in useFacilityDashboard's payload (2026-08-09
// backend council: inlining photos for every asset's visit history into
// the dashboard-wide aggregate multiplies unboundedly with org age for
// content a manager views on maybe 1-2 stores per session).
//
// staleTime is a flat 60 s, not Infinity once the visit is terminal.
// The photos and their item verdicts are frozen then (reports.signedAt /
// closedAt gates every mutating endpoint, CLAUDE.md §5; ADR-0098 for
// attestation-closed facility visits), but each card's `observation`
// overlay is the LIVE lifecycle of the finding that acta materialized —
// it moves to Asignada / Resuelta / Cerrada long after the visit closed.
// The thread mutations invalidate this key too (invalidateThread); the
// window covers changes made by someone else.
//
// The per-photo review layer (ADR-0169) moves on a closed acta too: AI
// proposals arrive after the close, and a supervisor's confirmations —
// and the auto-annul they can trigger — land on `review`, the group's
// review fields and its `observation`. useReviewPhotoMutation writes the
// server's answer into this cache and invalidates it; someone else's
// confirmation shows up within the same window.
export const VISIT_PHOTOS_STALE_MS = 60_000;

export function useVisitPhotos(reportId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.visitPhotos(reportId ?? ""),
    queryFn: () => reportsApi.getPhotos(reportId as string),
    enabled: !!reportId,
    staleTime: VISIT_PHOTOS_STALE_MS,
  });
}

// --- Closed-report view (/reportes/:id) --------------------------------
// The report row, its bloques and their items are all frozen once the
// report is terminal (reports.signedAt / closedAt gates every mutating
// endpoint, CLAUDE.md §5; ADR-0098 extends this to attestation-closed
// facility visits), so staleTime: Infinity is correct for the bloque
// and item reads, which the route only fires once the report is known
// to be terminal. The detail row is the one read that also fires for an
// in-progress visit (the route renders its «en curso» state from it),
// so its staleTime is a function of the row: permanent once terminal,
// otherwise the app's ordinary window — with refetchOnWindowFocus off
// globally, a blanket Infinity would pin a manager who opened the link
// two minutes before the supervisor closed the visit on that screen
// until a hard reload.

export function useReportDetail(reportId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.reportDetail(reportId ?? ""),
    queryFn: () => reportsApi.getDetail(reportId as string),
    enabled: !!reportId,
    staleTime: (query) => {
      const report = query.state.data?.report;
      return report &&
        isReportTerminal({ status: report.status, closedAt: report.closedAt ?? null })
        ? Number.POSITIVE_INFINITY
        : 30_000;
    },
  });
}

// `enabled` lets the caller hold this back until the report is known
// to be terminal — same shape as useFacilityDashboard's flag.
export function useReportBlocks(reportId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.reportBlocks(reportId ?? ""),
    queryFn: () => reportsApi.getBlocks(reportId as string),
    enabled: !!reportId && enabled,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export type ReportBlockItemsState = {
  /** Loaded item sets keyed by inspectionAreaId. */
  byArea: Record<string, BlockItemsResponse>;
  /** True while any area is still on its first fetch. */
  isLoading: boolean;
  /** Per-area failure, keyed by inspectionAreaId (absent = no error). */
  errors: Record<string, unknown>;
  /** Per-area refetch, so a failed section retries alone. */
  refetch: Record<string, () => void>;
};

// One query per bloque rather than one aggregate: the server exposes
// items per area only, and per-area queries let a single failed section
// show an inline retry while the others stay rendered. `combine` folds
// the array back into per-area maps so the screen never indexes by
// array position (which would drift if areaIds changed order). The
// combiner is memoised on areaIds: TanStack re-runs an inline combine
// on every render, which would hand the caller a fresh `byArea` object
// each time and defeat any effect keyed on it.
export function useReportBlockItems(
  reportId: string | undefined,
  areaIds: readonly string[]
): ReportBlockItemsState {
  const combine = useCallback(
    (results: UseQueryResult<BlockItemsResponse>[]): ReportBlockItemsState => {
      const byArea: Record<string, BlockItemsResponse> = {};
      const errors: Record<string, unknown> = {};
      const refetch: Record<string, () => void> = {};
      let isLoading = false;
      results.forEach((r, i) => {
        const areaId = areaIds[i];
        if (!areaId) return;
        if (r.data) byArea[areaId] = r.data;
        if (r.error) errors[areaId] = r.error;
        if (r.isLoading) isLoading = true;
        refetch[areaId] = () => void r.refetch();
      });
      return { byArea, isLoading, errors, refetch };
    },
    [areaIds]
  );
  return useQueries({
    queries: areaIds.map((areaId) => ({
      queryKey: queryKeys.reportBlockItems(reportId ?? "", areaId),
      queryFn: () => reportsApi.getBlockItems(reportId as string, areaId),
      enabled: !!reportId,
      staleTime: Number.POSITIVE_INFINITY,
    })),
    combine,
  });
}

// «Estado hoy» is the only live data on the closed-report view — a
// short staleTime so a manager who resolves an observation in another
// tab sees the pill move within a minute, without polling.
// ADR-0150 — el contacto para visitas de una unidad (arrendataria), en la
// asignación en lote. Ventana corta: es un dato que el propietario edita.
export function useVisitContact(assetId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.visitContact(assetId ?? ""),
    queryFn: () => assetsApi.getVisitContact(assetId as string),
    enabled: enabled && !!assetId,
    staleTime: 30_000,
  });
}

export function useAssetObservations(assetId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.assetObservations(assetId ?? ""),
    queryFn: () => assetsApi.getObservations(assetId as string),
    enabled: !!assetId,
    staleTime: 60_000,
  });
}

// The asset's current state per section (ADR-0137), behind DetalleTienda's
// «Estado por sección» strip. Not Infinity like useVisitPhotos: a new
// closed acta for the store replaces the source, so it follows the
// app's ordinary window, same as useAssetObservations.
export function useAssetState(assetId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.assetState(assetId ?? ""),
    queryFn: () => assetsApi.getState(assetId as string),
    enabled: !!assetId,
    staleTime: 60_000,
  });
}

// --- Admin configuration surface (ADR-0079) ----------------------------
// The "asignar checklists a tiendas" admin screen: the template list and
// the per-template assigned-stores drawer.

// The admin template list, incl. archived — org-scoped write roles only
// (core-api 403s otherwise), so callers gate `enabled` on the caller's
// role the same way useFacilityDashboard's callers gate on recipe.
export function useChecklistTemplatesAdminList(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.checklistTemplatesAdminList,
    queryFn: () => checklistTemplatesApi.listAdmin(),
    enabled,
  });
}

// Which stores carry a given template. refetchOnMount: "always" narrows
// — does not close — ADR-0079's accepted lost-update risk on the PUT
// full-replace below: two admins opening the same template's drawer
// within this query's staleTime would otherwise both write from a stale
// read. This does not eliminate the race (a second admin can still open
// the drawer mid-edit), it only shrinks the window to "since last mount"
// instead of "since last global refetch."
export function useChecklistTemplateAssets(templateId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.checklistTemplateAssets(templateId ?? ""),
    queryFn: () => checklistTemplatesApi.getAssets(templateId as string),
    enabled: !!templateId,
    refetchOnMount: "always",
  });
}

// PUT full replace of a template's assigned stores. The response already
// carries the authoritative new assignment set, so onSuccess seeds the
// cache directly instead of refetching — and invalidates the admin list
// for its assignedAssetCount column.
//
// Deliberately does NOT invalidate queryKeys.facilityDashboard: that
// payload carries no checklist-assignment field, so invalidating it
// would only cost a large-payload refetch for nothing.
//
// Also deliberately does NOT invalidate assetChecklistTemplates(assetId)
// for any asset — that's the union of the pre-edit and post-edit asset
// id sets, and only the calling drawer has both in scope (this hook's
// onSuccess only sees the post-edit set). Leave that invalidation to the
// caller.
export function useUpdateChecklistTemplateAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, assetIds }: { templateId: string; assetIds: string[] }) =>
      checklistTemplatesApi.putAssets(templateId, { assetIds }),
    onSuccess: (data, { templateId }) => {
      qc.setQueryData(queryKeys.checklistTemplateAssets(templateId), data);
      void qc.invalidateQueries({ queryKey: queryKeys.checklistTemplatesAdminList });
    },
  });
}

// PATCH label / cadenceDays / displayOrder / active / scope (+ the
// co-transactional assetIds a brand -> assets scope flip requires).
// The response is a ChecklistTemplateAdminSummary — it does not carry
// the assigned-assets shape checklistTemplateAssets(templateId) caches,
// so that key is invalidated rather than seeded.
export function useUpdateChecklistTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, body }: { templateId: string; body: ChecklistTemplateUpdateBody }) =>
      checklistTemplatesApi.patch(templateId, body),
    onSuccess: (_data, { templateId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.checklistTemplatesAdminList });
      void qc.invalidateQueries({ queryKey: queryKeys.checklistTemplateAssets(templateId) });
    },
  });
}

// Org roster stays stable within a session (membership changes are rare
// relative to page views) — tuned staleTime avoids every consumer (Equipo,
// the finding-timeline actor-name resolution) re-fetching on mount.
// Precedent: useRecipe() below.
export function useMemberships() {
  return useQuery({
    queryKey: queryKeys.memberships,
    queryFn: () => authApi.listMemberships(),
    staleTime: 5 * 60 * 1000,
  });
}

// Equipo.tsx's roster render — the opt-in `view=roster` filter (recipe-
// declared roles only, plus the server-computed `isOwner` field). Kept
// as its own hook/query key rather than a param on `useMemberships`, so
// `useMemberships`'s default (unfiltered) cache entry and behavior never
// change — DetalleObservacion.tsx's actor-name resolution depends on
// reading the unfiltered roster via that hook.
export function useRoster() {
  return useQuery({
    queryKey: queryKeys.roster,
    queryFn: () => authApi.listMemberships({ view: "roster" }),
    staleTime: 5 * 60 * 1000,
  });
}

// POST /organizations/me/invitations from the Equipo screen (ADR-0116,
// supersedes the ADR-0112-era direct call to auth-svc's
// POST /auth/invitations) — named "Team" to disambiguate from
// features/admin/queries.ts's useCreateInvitation, which hits the
// platform-admin's POST /admin/orgs/:id/invitations, a sibling core-api
// route with the same asset-stamping shape but keyed on an explicit org
// id instead of the caller's own. This route fans out to auth-svc's
// POST /auth/invitations after applying the recipe/cardinality/
// containment checks and the per-store role-attribute stamp — ADR-0112's
// escalation cap and ADR-0115's multibrand gate stay enforced exactly as
// before, unchanged, on that fanned-to endpoint. Invalidates the
// memberships roster; the new member won't actually appear there until
// they accept, but this keeps the cache consistent with any other
// org-scoped side effects the endpoint gains later. No pending-
// invitations list exists yet to invalidate.
export function useCreateTeamInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OrgSelfServiceInvitationCreateRequest) =>
      organizationsApi.createInvitation(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.memberships });
      void qc.invalidateQueries({ queryKey: queryKeys.roster });
    },
  });
}

// POST /assets/:id/owner-invitation (ADR-0124) — "activar la unidad ES
// invitar a su propietario", from the proyecto detail table.
//
// The idempotency key is a mutation *variable*, not something this hook
// mints: the drawer holds one key per mount so a retry after a lost
// response or a fan-out failure replays the same key instead of minting
// a second invitation to the same address (#0472).
//
// Invalidates the whole cartera-dashboard prefix rather than one key:
// the activated row's assetStatus changed, and so did the project's
// aggregates on the groups list — and both keys are parameterized by a
// query object this hook doesn't have. No optimistic patch: the server
// owns the destination status (recipe.activationAssetStatus), and a 409
// means the row moved under us, so the refetch IS the reconciliation.
export function useActivateUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      assetId,
      body,
      idempotencyKey,
    }: {
      assetId: string;
      body: AssetOwnerInvitationCreateRequest;
      idempotencyKey: string;
    }) => assetsApi.inviteOwner(assetId, body, idempotencyKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.carteraDashboardAll });
    },
  });
}

// F9 (corte 8) — fijar la entrega de una unidad; la tabla de unidades se
// vuelve a pedir para mostrar la fecha nueva.
export function useSetUnitDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      assetId,
      body,
      idempotencyKey,
    }: {
      assetId: string;
      body: UnitDeliveryBody;
      idempotencyKey: string;
    }) => assetsApi.setDelivery(assetId, body, idempotencyKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.carteraDashboardAll });
    },
  });
}

// Recipe stays stable for the lifetime of a session (it's a compile-time
// constant per org, not per-request state) — a long staleTime avoids
// every screen re-fetching it on mount.
export function useRecipe() {
  return useQuery({
    queryKey: queryKeys.recipe,
    queryFn: () => recipesApi.me(),
    staleTime: 5 * 60 * 1000,
  });
}

// The org row changes on the order of never within a session — same
// staleTime posture as useRecipe()/useMemberships().
export function useMyOrganization() {
  return useQuery({
    queryKey: queryKeys.myOrganization,
    queryFn: () => organizationsApi.me(),
    staleTime: 5 * 60 * 1000,
  });
}

// Resolve scope uuids to human names per ADR-0022. Disabled when the
// caller has no ids to look up — the hook is safe to call from any
// screen that consumes memberships without forcing a network round-trip
// when nothing is scoped.
export function useResolveScopes(req: AssetsResolveRequest) {
  const hasAny =
    (req.brandIds?.length ?? 0) + (req.assetGroupIds?.length ?? 0) + (req.assetIds?.length ?? 0) >
    0;
  return useQuery({
    queryKey: queryKeys.resolveScopes(req),
    queryFn: () => assetsApi.resolve(req),
    enabled: hasAny,
  });
}

// GET /assets/assignable (ADR-0116) — the store picker's source for the
// Equipo invite form. Disabled unless the caller can invite AND the
// currently-selected invite role is asset-scoped, mirroring the enabled
// gating convention used by useFacilityDashboard/useChecklistTemplatesAdminList
// above (a single un-gated mount would 403 for callers who can't invite).
// ADR-0165 — the supervisors' brands of affiliation (Equipo roster), and
// the edit. Enabled only for callers who may edit (org-scoped roles): the
// route answers 403 to everyone else.
export function useBrandAffiliations(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.brandAffiliations,
    queryFn: () => organizationsApi.listBrandAffiliations(),
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useSetBrandAffiliation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { userId: string; brandId: string }) =>
      organizationsApi.setBrandAffiliation(vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.brandAffiliations });
    },
  });
}

export function useAssignableAssets(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.assignableAssets,
    queryFn: () => assetsApi.listAssignable(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// staleTime tiers match the existing Tiendas/DetalleTienda cartera
// convention: 60s for the list, 5min for a single project's detail.
export function useCarteraProyectos(query: CarteraDashboardGroupsQuery = {}) {
  return useQuery({
    queryKey: queryKeys.carteraProyectos(query),
    queryFn: () => carteraDashboardApi.groups(query),
    staleTime: 60 * 1000,
  });
}

export function useCarteraProyectoDetail(
  id: string | undefined,
  query: CarteraDashboardAssetsQuery = {}
) {
  return useQuery({
    queryKey: queryKeys.carteraProyectoDetail(id ?? "", query),
    queryFn: () => carteraDashboardApi.assets(id!, query),
    enabled: id !== undefined,
    staleTime: 5 * 60 * 1000,
  });
}

// GET /findings/:id (ADR-0085) — the observación detail screen's read.
// staleTime matches useCarteraProyectoDetail's convention for a single
// detail record.
export function useFinding(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.finding(id ?? ""),
    queryFn: () => getFinding(id!),
    enabled: id !== undefined,
    staleTime: 5 * 60 * 1000,
  });
}

// ADR-0105/ADR-0129 — the postventa observation lifecycle mutations.
// Invalidation covers the detail (its status + timeline just changed)
// and the org-wide feed (its pill + filters read the same value). No
// optimistic patch: the transition graph lives server-side and a 409
// means the row moved under us — the refetch IS the reconciliation.
export function useUpdateObservationStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: FindingObservationStatusBody }) =>
      updateFindingObservationStatus(id, body),
    onSettled: (_data, _err, vars) => invalidateThread(qc, [vars.id]),
  });
}

/** F8 — agendar o cambiar la visita: el hilo gana su hito y la franja la
 *  fecha, así que se invalida como cualquier movimiento del hilo. */
export function useScheduleVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
      idempotencyKey,
    }: {
      id: string;
      body: FindingVisitBody;
      idempotencyKey: string;
    }) => scheduleFindingVisit(id, body, { idempotencyKey }),
    onSettled: (_data, _err, vars) => invalidateThread(qc, [vars.id]),
  });
}

// Everything that renders the thread of a finding (ADR-0134: facility's
// aggregate carries the thread fields too). An invalidation of a key no
// screen is observing only marks it stale — no request for a vertical
// that never mounted it.
function invalidateThread(qc: ReturnType<typeof useQueryClient>, ids: readonly string[]) {
  for (const id of ids) void qc.invalidateQueries({ queryKey: queryKeys.finding(id) });
  void qc.invalidateQueries({ queryKey: queryKeys.postventaObservations });
  // ADR-0142 — los conteos de cierre por motivo y «Propietarios sin
  // contacto» se leen de las mismas filas: cerrar, reabrir o mover una
  // observación los cambia (prefijo: cubre el de cada proyecto).
  void qc.invalidateQueries({ queryKey: queryKeys.postventaClosureSummary });
  // The project cards count open / unassigned / awaiting-conformity
  // observations from the same rows, so they move with the status.
  void qc.invalidateQueries({ queryKey: queryKeys.carteraDashboardAll });
  // Facility: the org-wide list, «Estado hoy», the store's open queue and
  // the KPIs all read GET /facility-dashboard.
  void qc.invalidateQueries({ queryKey: queryKeys.facilityDashboard });
  // The store carousel's cards carry each finding's state overlay
  // (GET /reports/:id/photos). Which report a finding came from isn't
  // known here, so every visit-photos query goes — only the mounted one
  // refetches.
  void qc.invalidateQueries({ predicate: isVisitPhotosQuery });
}

function isVisitPhotosQuery(q: { queryKey: readonly unknown[] }): boolean {
  const key = queryKeys.visitPhotos("");
  return q.queryKey.length === key.length && q.queryKey[0] === key[0] && q.queryKey[2] === key[2];
}

// POST /reports/:id/photo-reviews (ADR-0169) — a supervisor's decision on
// ONE photo of a closed acta. No optimistic patch: the server decides
// whether the decision annuls the observation, so the cache only takes
// its answer. The thread (lists, KPIs, the finding) only moves when this
// request annulled — or when the outcome is unknown (network, 5xx); a
// 4xx wrote nothing.
export type ReviewPhotoVars = {
  body: PhotoReviewRequest;
  /** One per user decision; a retry of the same decision reuses it. */
  idempotencyKey: string;
  /** The group's finding, for the thread invalidation on annul. */
  findingId: string | null;
};

export function useReviewPhotoMutation(reportId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: ReviewPhotoVars) =>
      reportsApi.reviewPhoto(reportId, vars.body, vars.idempotencyKey),
    onSuccess: (data, vars) => {
      qc.setQueryData<ReportPhotosResponse>(queryKeys.visitPhotos(reportId), (old) =>
        old ? mergePhotoReview(old, vars.body, data) : old
      );
    },
    onSettled: (data, err, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.visitPhotos(reportId) });
      const unknownOutcome =
        err != null && !(err instanceof ApiError && err.status >= 400 && err.status < 500);
      if (data?.annulled || unknownOutcome) {
        invalidateThread(qc, vars.findingId ? [vars.findingId] : []);
      }
    },
  });
}

function mergePhotoReview(
  old: ReportPhotosResponse,
  body: PhotoReviewRequest,
  res: PhotoReviewResponse
): ReportPhotosResponse {
  const group = old.groups.find((g) => g.id === body.resultId);
  if (!group) return old;
  const ids = new Set(group.photoIds);
  return {
    ...old,
    photos: old.photos.map((p) =>
      ids.has(p.id) && p.url === body.photoUrl ? { ...p, review: res.review } : p
    ),
    groups: old.groups.map((g) => (g.id === res.group.id ? { ...g, ...res.group } : g)),
  };
}

// POST /findings/:id/conformity — the verdict on a resolved observation
// (ADR-0105 Decisión 4, ADR-0134 §1/§5). Conforme stamps the closure
// overlay; No conforme reopens with a new cycle. Same no-optimistic-patch
// posture as the status transition: the server owns the graph.
export function useConformity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: FindingConformityBody }) =>
      postFindingConformity(id, body),
    onSettled: (_data, _err, vars) => invalidateThread(qc, [vars.id]),
  });
}

// ADR-0129 — the assign modal's classification half ("la asignación ES
// la clasificación de especialidad", spec §3). The server journals the
// (sugerencia, final) pair and re-derives severity; this client never
// computes either.
export function useClassifyFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: FindingClassificationBody }) =>
      classifyFinding(id, body),
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.finding(vars.id) });
      void qc.invalidateQueries({ queryKey: queryKeys.postventaObservations });
      // Gate de confianza: a human classification lifts "por revisar",
      // so the D1 tile / sidebar count must move with it.
      void qc.invalidateQueries({ queryKey: queryKeys.postventaTaxonomyMetrics });
    },
  });
}

// ADR-0145 — la bandeja de consultas del proyecto (GET /owner-inquiries
// ?assetGroupId=). Sin staleTime propio: el punto «nueva» y el estado se
// leen frescos al volver a la pantalla.
export function useOwnerInquiries(assetGroupId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.ownerInquiries(assetGroupId ?? ""),
    queryFn: () => getOwnerInquiries(assetGroupId!),
    enabled: assetGroupId !== undefined,
  });
}

// GET /owner-inquiries/:id — el hilo. `lastReadAt` del wire es la marca
// PREVIA del lector: el drawer la usa para «N nuevos» y después avanza la
// marca con useMarkOwnerInquiryRead, que a propósito no invalida este
// detalle (el separador se quedaría sin su referencia mientras el hilo
// sigue abierto). `refetchOnMount: "always"` (ADR-0155): el enlace del
// correo «El propietario respondió» abre el drawer y tiene que traer el
// mensaje nuevo aunque el hilo esté en caché dentro del staleTime global.
// ADR-0158 — casi en vivo: mientras el drawer (el único que observa esta
// query) está abierto y la pestaña visible, se revisa cada 15 s; con la
// pestaña oculta no corre (`refetchIntervalInBackground: false`), y al
// volver a ella se revisa al tiro (`refetchOnWindowFocus: "always"`, que
// pasa por encima del `false` global). Un 403/404 la detiene.
export function useOwnerInquiry(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.ownerInquiry(id ?? ""),
    queryFn: () => getOwnerInquiry(id!),
    enabled: id !== undefined,
    refetchOnMount: "always",
    refetchInterval: (q) => intervaloDelHilo(q.state.error),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: "always",
  });
}

// ADR-0158 — GET …/attachments/urls para los adjuntos de UN mensaje, en
// lotes de 24 (secuenciales). Sin intervalo: una miniatura que ya cargó no
// necesita una URL vigente, y re-firmar cambiaría su `src`. La miniatura
// rota (URL vencida) pide un refetch.
export function useOwnerInquiryAttachmentUrls(id: string, ids: readonly string[]) {
  return useQuery({
    queryKey: queryKeys.ownerInquiryAttachmentUrls(id, ids),
    queryFn: async () => {
      const out = new Map<string, string>();
      for (let i = 0; i < ids.length; i += OWNER_INQUIRY_ATTACHMENT_URLS_BATCH_MAX) {
        const lote = ids.slice(i, i + OWNER_INQUIRY_ATTACHMENT_URLS_BATCH_MAX);
        const res = await getOwnerInquiryAttachmentUrls(id, lote);
        for (const u of res.urls) out.set(u.id, u.url);
      }
      return out;
    },
    enabled: ids.length > 0,
    staleTime: 90_000,
  });
}

// POST /owner-inquiries/:id/messages. La clave de idempotencia es una
// variable de la mutación (el drawer la ata al texto, como la PWA). Sin
// patch optimista: el servidor fija `firstResponseAt`, el estado y la
// marca de lectura del autor, y la refetch es la reconciliación.
// Invalida lista + detalle (prefijo) y el rollup de cartera, de donde el
// rail lee el badge de consultas sin responder. Las URL firmadas de los
// adjuntos NO (ADR-0158): re-firmarlas cambiaría el `src` de cada
// miniatura ya cargada, la volvería a descargar (`no-store`) y gastaría el
// cupo de 60 pedidos por minuto de la ruta de URLs en un hilo con muchas
// fotos. La miniatura o el visor que fallan se re-firman solos.
export function useReplyOwnerInquiry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
      idempotencyKey,
    }: {
      id: string;
      body: OwnerInquiryReplyRequest;
      idempotencyKey: string;
    }) => replyOwnerInquiry(id, body, { idempotencyKey }),
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.ownerInquiriesAll,
        predicate: (q) => q.queryKey[1] !== "attachment-urls",
      });
      void qc.invalidateQueries({ queryKey: queryKeys.carteraDashboardAll });
    },
  });
}

// POST /owner-inquiries/:id/read — «hasta aquí leí». Sólo las listas:
// el conteo de no leídos de la fila baja; el hilo abierto no se toca.
export function useMarkOwnerInquiryRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, lastReadAt }: { id: string; lastReadAt: string }) =>
      markOwnerInquiryRead(id, lastReadAt),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.ownerInquiriesLists });
    },
  });
}

export function usePostventaObservations(assetGroupId?: string) {
  return useQuery({
    queryKey: assetGroupId
      ? [...queryKeys.postventaObservations, assetGroupId]
      : queryKeys.postventaObservations,
    queryFn: () => getPostventaObservations(assetGroupId),
  });
}

// ADR-0142 — conteo de cierres por motivo y propietarios sin contacto.
// `enabled` lo pasa la pantalla (sólo postventa con ciclo de
// observaciones); un fallo deja la sección fuera, la pantalla sigue.
export function usePostventaClosureSummary(assetGroupId?: string, enabled = true) {
  return useQuery({
    queryKey: assetGroupId
      ? [...queryKeys.postventaClosureSummary, assetGroupId]
      : queryKeys.postventaClosureSummary,
    queryFn: () => getPostventaClosureSummary(assetGroupId),
    enabled,
  });
}

// ADR-0153 — el panel de encuestas del proyecto (y el tile NPS de
// Unidades). `enabled` lo pasa la pantalla (rol y receta con encuestas).
export function usePostventaSurveySummary(assetGroupId?: string, enabled = true) {
  return useQuery({
    queryKey: assetGroupId
      ? [...queryKeys.postventaSurveySummary, assetGroupId]
      : queryKeys.postventaSurveySummary,
    queryFn: () => getPostventaSurveySummary(assetGroupId),
    enabled,
  });
}

// ADR-0154 — el último análisis de cartera guardado del alcance. Un 403
// (rol o vertical sin análisis) no se reintenta: la pantalla lo muestra.
export function usePostventaAnalisis(assetGroupId?: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.postventaAnalisis(assetGroupId),
    queryFn: () => getPostventaAnalisis(assetGroupId),
    enabled,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
  });
}

// Cada generación cuesta y cuenta contra la cuota: nunca se reintenta
// sola. La respuesta reemplaza al informe guardado del mismo alcance.
export function useGenerarPostventaAnalisis(assetGroupId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => generarPostventaAnalisis(assetGroupId),
    retry: false,
    onSuccess: (res) => {
      qc.setQueryData(queryKeys.postventaAnalisis(assetGroupId), res);
    },
  });
}

// #0698 — instrumentación de la taxonomía (cola OTR + gate de
// confianza). Falla silenciosa a nivel de card: la pantalla de la cola
// no depende de esta query para operar.
export function usePostventaTaxonomyMetrics(enabled = true) {
  return useQuery({
    queryKey: queryKeys.postventaTaxonomyMetrics,
    queryFn: getPostventaTaxonomyMetrics,
    // The sidebar mounts on every vertical; the route is gated
    // server-side on the observation lifecycle, so callers outside the
    // postventa screens pass the recipe gate here.
    enabled,
  });
}

export function useShareBundles() {
  return useQuery({
    queryKey: queryKeys.shareBundles,
    queryFn: () => shareBundlesApi.list(),
  });
}

export function useShareBundleAccesses(bundleId: string | null) {
  return useQuery({
    queryKey: ["share-bundles", bundleId, "accesses"] as const,
    queryFn: () => shareBundlesApi.accesses(bundleId!),
    enabled: bundleId !== null,
  });
}

export function useCreateShareBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ShareBundleCreateBody) => shareBundlesApi.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.shareBundles });
    },
  });
}

export function useRevokeShareBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shareBundlesApi.revoke(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.shareBundles });
    },
  });
}

// ── Nómina de contratistas y asignación real ────────────────────────
export function useProviders(enabled = true) {
  return useQuery({
    queryKey: queryKeys.providers,
    queryFn: listProviders,
    enabled,
  });
}

export function useCreateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProviderCreateBody) => createProvider(body),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.providers });
    },
  });
}

/** Asignar mueve el estado, escribe el proveedor y emite el enlace en un
 *  solo acto, así que invalida las tres superficies que muestran
 *  cualquiera de esas tres cosas — incluida la nómina, cuyo conteo de
 *  «trabajos activos» acaba de cambiar. */
export function useAssignFinding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: FindingAssignBody }) => assignFinding(id, body),
    onSettled: (_data, _err, vars) => {
      // «Sin asignar» on the project cards just went down by one.
      invalidateThread(qc, [vars.id]);
      void qc.invalidateQueries({ queryKey: queryKeys.providers });
    },
  });
}

/** El lote. Invalida cada hilo tocado por id — el detalle de cualquiera
 *  de las seleccionadas tiene que mostrar su hito de asignación (y el de
 *  la aceptación implícita) sin que el jefe recargue. */
export function useAssignFindingBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FindingAssignBatchBody) => assignFindingBatch(body),
    onSettled: (_data, _err, vars) => {
      // «Sin asignar» on the project cards just went down by the batch.
      invalidateThread(qc, vars.findingIds);
      void qc.invalidateQueries({ queryKey: queryKeys.providers });
    },
  });
}

// ADR-0163 (#0770) — convertir una consulta en observación. La sugerencia
// no escribe nada: sin Idempotency-Key ni invalidaciones.
export function useSuggestOwnerInquiryConversion() {
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: OwnerInquiryConversionSuggestionRequest }) =>
      suggestOwnerInquiryConversion(id, body),
  });
}

/** La conversión. La clave la ata el formulario al contenido (un reintento
 *  del mismo envío reutiliza la clave). Al terminar se refrescan la bandeja
 *  y el hilo (la consulta quedó cerrada), el hilo de la observación nueva,
 *  las métricas del panel y la lista de la unidad. */
export function useConvertOwnerInquiry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
      idempotencyKey,
    }: {
      id: string;
      body: OwnerInquiryConversionRequest;
      idempotencyKey: string;
      assetId: string;
    }) => convertOwnerInquiry(id, body, { idempotencyKey }),
    // El hilo queda cerrado YA, con lo que trae la respuesta: sin esto el
    // drawer volvía un momento al hilo abierto (caja «Responder» y botón
    // «Convertir») hasta que llegaba el refetch.
    onSuccess: (data, vars) => {
      qc.setQueryData<OwnerInquiryDetail>(
        queryKeys.ownerInquiry(vars.id),
        (prev) =>
          prev && {
            ...prev,
            inquiry: data.inquiry,
            entries: prev.entries.some((e) => e.id === data.message.id)
              ? prev.entries
              : [...prev.entries, data.message],
            canConvert: false,
            canSuggest: false,
          }
      );
    },
    onSettled: (data, _err, vars) => {
      void qc.invalidateQueries({
        queryKey: queryKeys.ownerInquiriesAll,
        predicate: (q) => q.queryKey[1] !== "attachment-urls",
      });
      invalidateThread(qc, data ? [data.conversion.findingId] : []);
      void qc.invalidateQueries({ queryKey: queryKeys.postventaTaxonomyMetrics });
      void qc.invalidateQueries({ queryKey: queryKeys.assetObservations(vars.assetId) });
    },
  });
}
